import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useAppContext } from '../AppContext';
import { httpsCallable } from 'firebase/functions';
import MarkdownRenderer from './MarkdownRenderer';

const HarmonyHelperModal = ({ onClose }) => {
    const { appFunctions } = useAppContext();
    const [messages, setMessages] = useState([
        { id: 1, sender: 'ai', text: "Welcome to the Harmony Helper! I'm here to answer any questions you have about Whispers of Harmony. How can I assist you?" }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        // --- START MODIFICATION ---
        // Check if this is the first interaction and set the flag in localStorage.
        const hasInteracted = localStorage.getItem('hasInteractedWithChatbot');
        if (!hasInteracted) {
            localStorage.setItem('hasInteractedWithChatbot', 'true');
        }
        // --- END MODIFICATION ---

        const userMessage = { id: Date.now(), sender: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        const currentInput = input;
        setInput('');
        setIsLoading(true);

        try {
            const askHarmonyHelper = httpsCallable(appFunctions, 'askHarmonyHelper');
            const result = await askHarmonyHelper({ question: currentInput });
            
            const aiText = result?.data?.answer;
            const aiMessage = { 
                id: Date.now() + 1, 
                sender: 'ai', 
                text: aiText || "I'm not sure how to answer that. Could you try rephrasing your question?" 
            };
            setMessages(prev => [...prev, aiMessage]);

        } catch (error) {
            console.error("Error calling askHarmonyHelper:", error);
            const errorMessage = { id: Date.now() + 1, sender: 'ai', text: "Sorry, I'm having a little trouble connecting to my knowledge base right now. Please try again in a moment." };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <motion.div
                className="bg-gray-900/80 p-4 sm:p-6 rounded-2xl shadow-glow max-w-lg w-full h-[80vh] flex flex-col border border-purple-500/50"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
            >
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <LucideIcons.Sparkles size={24} className="text-purple-300" />
                        <h2 className="text-xl font-bold text-white font-playfair">Harmony Helper</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <LucideIcons.X size={24} />
                    </button>
                </div>

                <div className="flex-grow overflow-y-auto custom-scrollbar pr-2 space-y-4">
                    {messages.map(msg => (
                        <div key={msg.id} className={`flex gap-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.sender === 'ai' && <LucideIcons.Sparkles size={24} className="text-purple-400 flex-shrink-0" />}
                            <div className={`max-w-xs md:max-w-md p-3 rounded-2xl ${msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-200'}`}>
                                <MarkdownRenderer>{msg.text}</MarkdownRenderer>
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex gap-3 justify-start">
                            <LucideIcons.Sparkles size={24} className="text-purple-400 flex-shrink-0" />
                            <div className="max-w-xs md:max-w-md p-3 rounded-2xl bg-gray-800 text-gray-200">
                                <div className="flex items-center gap-2">
                                    <div className="dot-flashing"></div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSend} className="mt-4 flex-shrink-0">
                    <div className="relative">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about any feature..."
                            className="w-full bg-gray-800 border border-gray-700 rounded-full py-3 pl-4 pr-12 text-white focus:outline-none focus:border-purple-500"
                            disabled={isLoading}
                        />
                        <button type="submit" disabled={isLoading} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-purple-600 hover:bg-purple-700 rounded-full disabled:opacity-50">
                            <LucideIcons.Send size={20} className="text-white" />
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default HarmonyHelperModal;
